import React, { useReducer, useRef } from "react";
import { FilterContextProviderProps, FilterQueryStateHandler, FilterState } from "models/propTypes/filterPropTypes";
import { FilterReducer } from "./reducer";
import { ContextConstants, FormFieldConstants } from "utils/constants";
import { useNavigate, useLocation } from "react-router";
import { FilterQueryHelper, FormHelper, QueryStateHelper } from "utils/helpers";

export function useFilterStateInQuery<T extends Record<string, unknown>>(
  filterQueryStateHandler: FilterQueryStateHandler<T>,
  initialFilter: T
) {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);

  let queryState: any;

  if (queryState === undefined && Array.from(searchParams).length === 0) {
    queryState = { ...initialFilter };
  } else {
    const currentQueryState: Record<string, unknown> = {} as T;
    for (const prop of Object.keys(filterQueryStateHandler)) {
      const queryValue = filterQueryStateHandler[prop].urlDecode(searchParams);
      if (queryValue !== undefined) {
        currentQueryState[prop] = queryValue;
      }
    }

    queryState = currentQueryState;
  }

  function setQueryState(newFilter: any) {
    const queryStateUpdated: any = (searchParams as any).size === 0 ? {} : queryState;

    for (const prop of Object.keys(filterQueryStateHandler)) {
      filterQueryStateHandler[prop].urlEncode(searchParams, queryStateUpdated[prop], newFilter[prop]);
    }
    location.search = "?" + searchParams.toString();
    navigate(location, { replace: true });
  }

  function queryHasFilterProps() {
    return FilterQueryHelper.hasFilterPropsInQuery(filterQueryStateHandler, searchParams);
  }

  return { queryState, setQueryState, queryHasFilterProps };
}

export const FilterContext = React.createContext<FilterState<any>>({
  isOpen: false,
  filter: {},
  count: 0,
  setFilter: (filter: any) => {},
  toggleFilter: () => {},
  setFilterCount: (count: number) => {},
  queryHasFilterProps: () => false,
  hasUserChosenFilter: () => false,
});

function FilterContextProvider<T extends Record<string, any>>(
  props: React.PropsWithChildren<FilterContextProviderProps<T>>
) {
  const { queryState, setQueryState, queryHasFilterProps } = useFilterStateInQuery(
    {
      selectedFilter: QueryStateHelper.numberQueryEncoder(FormFieldConstants.Filter.FilterBase.SelectedFilter, true),
      ...(props.filterQueryStateHandler ?? ({} as FilterQueryStateHandler<T>)),
    },
    props.initialFilter ?? {}
  );

  const [state, dispatch] = useReducer(FilterReducer, {
    isOpen: false,
    filter: queryState ?? {},
    count: 0,
    setFilter: (filter: any, isInitialFilter?: boolean) => {},
    toggleFilter: () => {},
    setFilterCount: (count: number) => {},
    queryHasFilterProps: () => false,
    hasUserChosenFilter: () => false,
  });

  // Filter params already in the URL on mount came from a link, so they count as chosen. A page's own initial filter
  // (setFilter with isInitialFilter) writes to the URL too, which is why the URL alone cannot answer this later.
  const userHasChosenFilter = useRef<boolean | null>(null);
  if (userHasChosenFilter.current === null) {
    userHasChosenFilter.current = queryHasFilterProps();
  }

  const setFilter = async (filter: any, isInitialFilter?: boolean) => {
    if (!isInitialFilter) {
      userHasChosenFilter.current = true;
    }

    if (
      JSON.stringify(filter) !== JSON.stringify(state.filter) &&
      (!isInitialFilter || (isInitialFilter && !queryHasFilterProps()))
    ) {
      let isValid = true;
      if (props.validationSchema) {
        try {
          await FormHelper.validateYupSchema(filter, props.validationSchema);
        } catch (errors) {
          isValid = false;
        }
      }

      if (isValid) {
        dispatch({
          type: ContextConstants.FilterActions.SET_FILTER,
          payload: props.valueFormatter ? props.valueFormatter(filter) : filter,
        });
        if (props.filterQueryStateHandler) {
          setQueryState(filter);
        }
      }
    }
  };

  const toggleFilter = () => {
    dispatch({ type: ContextConstants.FilterActions.TOGGLE_FILTER });
  };

  const setFilterCount = (count: number) => {
    dispatch({ type: ContextConstants.FilterActions.SET_FILTER_COUNT, payload: count });
  };

  return (
    <FilterContext.Provider
      value={{
        ...state,
        filterCategory: props.filterCategory,
        reportToAPIFilterFormatter: props.reportToAPIFilterFormatter,
        apiToReportFilterFormatter: props.apiToReportFilterFormatter,
        setFilter,
        toggleFilter,
        setFilterCount,
        queryHasFilterProps,
        hasUserChosenFilter: () => userHasChosenFilter.current === true,
      }}
    >
      {props.children}
    </FilterContext.Provider>
  );
}

export default FilterContextProvider;
